import { element } from "protractor";
import { DataSource } from "@angular/cdk/table";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { CollectionViewer } from "@angular/cdk/collections";
import { catchError, finalize, tap, delay } from "rxjs/operators";

export class GenericDataSource implements DataSource<any> {
  private itemsSubject = new BehaviorSubject<any[]>([]);
  private itemsSelected = new BehaviorSubject<any[]>([]);
  private totalItemCount = new BehaviorSubject<number>(null);
  private isLoading = new BehaviorSubject<boolean>(false);
  private allPagesLoadedYN = new BehaviorSubject<boolean>(false);

  private currentIndex = 0;

  public itemsSubject$ = this.itemsSubject.asObservable();
  public itemsSelected$ = this.itemsSelected.asObservable();
  public totalItemCount$ = this.totalItemCount.asObservable();
  public isLoading$ = this.isLoading.asObservable();
  public allPagesLoadedYN$ = this.allPagesLoadedYN.asObservable();

  private previousSubscription: Subscription = null;

  constructor(
    private source: (currentIndex: number, pageSize: number) => Observable<any>,
    private itemsPropertyName: string = "Items",
    private totalItemsPropertyName: string = "TotalItems"
  ) {}

  connect(collectionViewer: CollectionViewer): Observable<any[]> {
    return this.itemsSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject.complete();
    this.itemsSelected.complete();
    this.totalItemCount.complete();
    this.isLoading.complete();
    this.allPagesLoadedYN.complete();
  }

  showNextPage(appendResultYN = false, startIndex = null, pageSize = 40) {
    if (
      /*this.isLoading.getValue() === false && */ !appendResultYN ||
      !this.allPagesLoadedYN.getValue()
    ) {
      if (this.previousSubscription) {
        this.previousSubscription.unsubscribe();
      }

      this.isLoading.next(true);

      if (!appendResultYN) {
        this.currentIndex = startIndex != null ? startIndex : 0;
        this.allPagesLoadedYN.next(false);
      }

      this.previousSubscription = this.source
        .call(this, this.currentIndex, pageSize)
        .pipe(
          catchError(() => {
            this.totalItemCount.next(0);
            return of([]);
          }),
          finalize(() => {
            this.isLoading.next(false);
          })
        )

        .subscribe((rawResult: any) => {
          const result = rawResult.Data;
          if (result) {
            this.currentIndex += Math.min(
              pageSize,
              result[this.itemsPropertyName]?.length
            );
            if (this.currentIndex >= result[this.totalItemsPropertyName]) {
              this.allPagesLoadedYN.next(true);
            }

            if (!appendResultYN) {
              this.itemsSubject.next(result[this.itemsPropertyName]);
              this.itemsSelected.next([]); // Reset selection when we do not append new items (displaying a new page of items for example)
            } else {
              // append new items to the already loaded list of items
              let currentArray = this.itemsSubject.getValue();
              currentArray = [
                ...(currentArray || []),
                ...result[this.itemsPropertyName],
              ];
              this.itemsSubject.next(currentArray);
            }
            this.totalItemCount.next(result[this.totalItemsPropertyName]);
          }
        });
    }
  }

  /** Return the selected items. */
  public get SelectedItems(): any[] {
    const currentArray = this.itemsSubject.getValue();
    return currentArray.filter(
      (item) => "IsSelected" in item && item.IsSelected
    );
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const currentArray = this.itemsSubject.getValue();
    const selectedItems = this.itemsSelected.getValue();
    return currentArray.length === selectedItems.length;
  }

  /** Whether at least one element is selected. */
  isAnySelected() {
    const selectedItems = this.itemsSelected.getValue();
    return selectedItems.length > 0;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterSelectionToggle() {
    const currentArray = this.itemsSubject.getValue();
    if (this.isAllSelected()) {
      currentArray.forEach((item) => (item.IsSelected = false));
      this.itemsSelected.next([]);
    } else {
      currentArray.forEach((item) => (item.IsSelected = true));
      this.itemsSelected.next(currentArray);
    }
  }

  /** Toggle selection of a specific item. */
  singleSelectionToggle(item: any) {
    const currentArray = this.itemsSubject.getValue();
    const itemIndex = currentArray.indexOf(item);
    if (itemIndex >= 0) {
      currentArray[itemIndex].IsSelected = !currentArray[itemIndex].IsSelected;
    }
    const selectedItems = currentArray.filter(
      (itemObj) => "IsSelected" in itemObj && itemObj.IsSelected
    );
    this.itemsSelected.next(selectedItems);
  }

  reset() {
    this.itemsSubject.next([]);
    this.totalItemCount.next(0);
    this.itemsSelected.next([]);
    this.isLoading.next(false);
    this.allPagesLoadedYN.next(false);
  }
}
